import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ShieldAlert } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(match[1]);

    (async () => {
      try {
        // Any non-JSON response (e.g. an HTML error page from a proxy misconfig,
        // or a 5xx with an empty body) is caught here and rendered as a
        // clean, professional error card rather than a raw stack trace.
        const res = await api.post("/auth/session", { session_id });
        if (!res || typeof res.data !== "object" || !res.data.user_id) {
          throw new Error("Unexpected sign-in response from the server");
        }
        setUser(res.data);
        window.history.replaceState({}, "", "/search");
        navigate("/search", { replace: true, state: { user: res.data } });
      } catch (e) {
        // Always clear the session_id from the URL so a refresh doesn't retry
        // the same (already-consumed) token.
        try { window.history.replaceState({}, "", "/login"); } catch (_) {}
        // v41d — mirror GoogleAuthCallback's strict handling. We trust
        // the server's `detail` text ONLY on 401 / 403; everything else
        // gets a generic friendly sentence so Cloudflare / proxy error
        // bodies never bleed into our "Sign-in blocked" card.
        let detail = "";
        try {
          const status = e?.response?.status;
          const body = e?.response?.data;
          if (status === 401 || status === 403) {
            if (body && typeof body === "object" && typeof body.detail === "string") {
              detail = body.detail;
            } else {
              detail = "Your Google sign-in session couldn't be verified. Please try again.";
            }
          } else if (status && status >= 500) {
            detail = "Temporary problem on our side — please try again in a moment.";
          } else if (status === 429) {
            detail = "Too many sign-in attempts. Please wait a few seconds and try again.";
          } else if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
            detail = "The server took too long to answer. Please try again in a moment.";
          } else if (e?.message?.includes("Network Error") || !e?.response) {
            detail = "Network error — check your internet connection and try again.";
          } else {
            detail = "Sign-in didn't go through. Please try again in a moment.";
          }
        } catch (_) { /* stay silent — we always show something */ }
        setError(detail || "Sign-in failed. Please try again.");
      }
    })();
  }, [location.hash, navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center border hairline rounded-md p-8" style={{ background: "var(--surface)" }} data-testid="auth-error-card">
          <ShieldAlert size={40} className="mx-auto text-red-400 mb-4" />
          <h1 className="display text-2xl font-semibold">Sign-in blocked</h1>
          <p className="text-sm text-zinc-300 mt-3" data-testid="auth-error-message">{error}</p>
          <button
            data-testid="auth-error-back"
            onClick={() => navigate("/login", { replace: true })}
            className="mt-6 text-xs px-4 py-2 rounded-md border hairline hover:border-white/30 transition-colors"
          >Back to sign-in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center text-zinc-500">
      <Loader2 className="animate-spin mr-2" size={18} /> Securing session…
    </div>
  );
}
